import { DateTime } from 'luxon';
import { listTimeZones } from 'timezone-support';

export type DateLike = DateTime | Date | string | undefined | null;

export class DateUtils {
  /**
   * Retornar data zero UTC compatível com DateTime
   */
  static null(): DateTime {
    return DateTime.utc(0, 0, 0, 0, 0);
  }

  /**
   * Retornar a data atual sem a hora
   */
  static currentDate(timezone?: string): DateTime {
    if (!timezone) {
      return DateTime.now().startOf('day');
    }
    return DateTime.now().setZone(timezone).startOf('day');
  }

  /**
   * Retornar a data e hora atual
   */
  static currentDateTime(): DateTime {
    return DateTime.now();
  }

  /**
   * Retornar a data atual UTC sem a hora
   */
  static currentDateUTC(): DateTime {
    return DateTime.now().toUTC().startOf('day');
  }

  /**
   * Retornar a data e hora atual UTC
   */
  static currentDateTimeUTC(): DateTime {
    return DateTime.now().toUTC();
  }

  /**
   *
   * Retornar um array de DateTime contendo cada data no período informado
   * @param weekdays Array de dias da semana que devem ser retornados. Usa ISO WEEK DAY, de 1 a 7, sendo um segunda e sete domingo
   *
   */
  static getRange(start: DateLike, end: DateLike, weekdays?: number[]): DateTime[] {
    const startAux = this.toDateTime(start);
    const endAux = this.toDateTime(end);
    const dates = [];
    for (let date = startAux; date <= endAux; date = date.plus({ days: 1 })) {
      // Só incluir no retorno os dias da semana informados ou todos
      if (!weekdays?.length || weekdays.includes(date.weekday)) {
        dates.push(date);
      }
    }
    return dates;
  }

  static getRangeDateUTC(start: DateLike, end: DateLike, weekdays?: number[]): DateTime[] {
    const startAux = this.startOfDayUTC(start);
    const endAux = this.startOfDayUTC(end);
    const dates = [];
    for (let date = startAux; date <= endAux; date = date.plus({ days: 1 })) {
      // Só incluir no retorno os dias da semana informados ou todos
      if (!weekdays?.length || weekdays.includes(date.weekday)) {
        dates.push(date);
      }
    }
    return dates;
  }

  static getRangeIsoDate({
    start,
    end,
    weekdays,
    timezone,
  }: {
    start: DateLike;
    end: DateLike;
    weekdays?: number[];
    timezone?: string;
  }): string[] {
    return this.getRangeDate({ start, end, weekdays, timezone })
      .map((e) => e.toISODate())
      .filter((d): d is string => d !== null);
  }

  static getRangeIsoDateUTC(start: DateLike, end: DateLike, weekdays?: number[]): string[] {
    return this.getRangeDateUTC(start, end, weekdays)
      .map((e) => e.toISODate())
      .filter((d): d is string => d !== null);
  }

  /**
   *
   * Retornar array de DateTime contendo cada data no período informado, mas sem a hora
   * @param weekdays Dias da semana, de zero a seis, que devem ser retornados, sendo zero domingo e seis sábado
   * Se nada for informado em weekdays irá retornar as datas de todos os dias da semana no peeríodo.
   * Se algum dia for informado, os dias da semana não informados não serão retornados no array de datas
   * @timezone Se informado, primeiro irá converter as datas de início e fim para o timezone informado, para então fazer o range
   *
   */
  static getRangeDate({
    start,
    end,
    weekdays,
    timezone,
  }: {
    start: DateLike;
    end: DateLike;
    weekdays?: number[];
    timezone?: string;
  }): DateTime[] {
    const zonedStart = this.toDateTime(start).setZone(timezone);
    const zonedEnd = this.toDateTime(end).setZone(timezone);
    const startAux = zonedStart.startOf('day');
    const endAux = zonedEnd.startOf('day');
    return this.getRange(startAux, endAux, weekdays);
  }

  /**
   * Retornar array de DateTime contendo o primeiro dia de cada mês no período informado, sem a hora
   */
  static getRangeMonth(start: DateLike, end: DateLike): DateTime[] {
    const startAux = this.startOfMonthUTC(start);
    const endAux = this.startOfMonthUTC(end);
    const dates: DateTime[] = [];
    for (let date = startAux; date <= endAux; date = date.plus({ months: 1 })) {
      dates.push(date);
    }
    return dates;
  }

  /**
   * Retornar array de DateTime contendo o primeiro dia de cada  no período informado, sem a hora
   */
  static getRangeYear(start: DateLike, end: DateLike): DateTime[] {
    const startAux = this.startOfYearUTC(start);
    const endAux = this.startOfYearUTC(end);
    const dates: DateTime[] = [];
    for (let date = startAux; date <= endAux; date = date.plus({ years: 1 })) {
      dates.push(date);
    }
    return dates;
  }

  /**
   * Transformar array de DateTime em Array de JSDate.
   * array estiver vazio retornará um array vazio
   */
  static DateTimesToJSDates(datetimes: DateTime[]): Date[] {
    if (!datetimes?.length) {
      return [];
    }
    return datetimes.map((date) => date.toJSDate());
  }

  static isJSDateString(dateString: string): boolean {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  /**
   * Transformar Date ou String Date em DateTime
   */
  static toDateTime(date: DateLike): DateTime {
    if (DateTime.isDateTime(date)) {
      return date;
    }

    if (typeof date === 'string') {
      if (this.isJSDateString(date)) {
        return DateTime.fromJSDate(new Date(date));
      }

      return DateTime.fromISO(date);
    }

    return DateTime.fromJSDate(date as Date);
  }

  static fromISO(date: string, timezone?: string): DateTime {
    return timezone ? DateTime.fromISO(date).setZone(timezone) : DateTime.fromISO(date);
  }

  static toDateTimeUTC(date: DateLike): DateTime {
    return DateUtils.toDateTime(date).toUTC();
  }

  /**
   * Retornar a data e hora inicial do mês
   */
  static startOfMonthUTC(date: DateLike): DateTime {
    return DateUtils.toDateTimeUTC(date).startOf('month').startOf('day');
  }

  static startOfMonth(date: DateLike, timezone?: string): DateTime {
    if (!timezone) {
      return DateUtils.toDateTime(date).startOf('month').startOf('day');
    }
    return DateUtils.toDateTime(date).setZone(timezone).startOf('month').startOf('day');
  }

  /**
   * Retornar da data e hora inicial do ano
   */
  static startOfYearUTC(date: DateLike): DateTime {
    return DateUtils.toDateTimeUTC(date).startOf('year').startOf('day');
  }

  static startOfYear(date: DateLike): DateTime {
    return DateUtils.toDateTime(date).startOf('year').startOf('day');
  }

  /**
   * Transformar a data informada para UTC sem a hora
   */
  static toDateUTC(date: DateLike): DateTime {
    return DateUtils.toDateTimeUTC(date).startOf('day');
  }

  /**
   * Retornar se a data informada é um final de semana.
   * @param date Aceita DateTime ou Date
   */
  static isWeekend(date: DateLike): boolean {
    return [6, 7].includes(this.toDateTime(date).weekday);
  }

  /**
   * Retornar a diferença em dias entre duas datas, ignorando a hora
   */
  static diffDays(dateLeft: DateLike, dateRight: DateLike): number {
    if (!dateLeft || !dateRight) {
      throw new Error('Fail to diff days. Dates to compare not provided');
    }
    const dt1 = this.toDateTime(dateLeft).startOf('day');
    const dt2 = this.toDateTime(dateRight).startOf('day');
    const result = dt1.diff(dt2, ['days', 'hours']).toObject().days ?? 0;
    return result;
  }

  /**
   * Retornar a diferença em dias entre duas datas, considerando a hora
   */
  static diffDates(dateLeft: DateLike, dateRight: DateLike): number {
    if (!dateLeft || !dateRight) {
      throw new Error('Fail to diff days. Dates to compare not provided');
    }
    const dt1 = this.toDateTime(dateLeft);
    const dt2 = this.toDateTime(dateRight);
    const result = dt1.diff(dt2, ['days', 'hours']).toObject().days;
    // Não foi possível comparar !result porque quando result = 0 retornava true
    if (result === undefined) {
      throw new Error('Invalid dates to compare');
    }
    return result;
  }

  /**
   * Retornar DateTime ou Date incrementando o número de anos informado
   */
  static plusYears(date: DateLike, years: number): DateTime {
    return this.toDateTime(date).plus({ years });
  }

  /**
   * Retornar DateTime ou Date incrementando o número de meses informado
   */
  static plusMonths(date: DateLike, months: number): DateTime {
    return this.toDateTime(date).plus({ months });
  }

  /**
   * Retornar DateTime ou Date incrementando o número de dias informado
   */
  static plusDays(date: DateLike, days: number): DateTime {
    return this.toDateTime(date).plus({ days });
  }

  /**
   * Retornar DateTime ou Date incrementando o número de horas informado
   */
  static plusHours(date: DateLike, hours?: number): DateTime {
    return this.toDateTime(date).plus({ hours });
  }

  /**
   * Retornar se a data informada é igual à data atual, ignorando a hora
   */
  static isToday(date: DateLike | undefined): boolean {
    if (!date) return false;
    const today = DateTime.utc().startOf('day').toISO();
    const dateTest = DateUtils.toDateTime(date).toUTC().startOf('day').toISO();
    return dateTest === today;
  }

  /**
   * Retornar se a data informada é igual ou anterior à data atual, ignorando a hora
   */
  static isTodayOrBefore(date: DateLike): boolean {
    const today = DateTime.utc().startOf('day');
    const dateTest = this.toDateTime(date).toUTC().startOf('day');
    return dateTest <= today;
  }

  /**
   * Retornar se a data informada é igual ou posterior à data atual, ignorando a hora
   */
  static isTodayOrAfter(date: DateLike): boolean {
    const today = DateTime.utc().startOf('day');
    const dateTest = this.toDateTime(date).startOf('day');
    return dateTest >= today;
  }

  /**
   * Retornar se a data informada é anterior à data atual, ignorando a hora
   * // ## Substituir pelo isBeforeToday2, testando as consequências, e depois remover este e remover o "2" do nome
   */
  static isBeforeToday(date: DateLike): boolean {
    const today = DateTime.utc().startOf('day');
    const dateTest = this.toDateTime(date).startOf('day');
    return dateTest < today;
  }

  /**
   * Retornar se a data informada é anterior à data atual, ignorando a hora
   */
  static isBeforeToday2(date: DateLike): boolean {
    const today = DateUtils.currentDate();
    const dateTest = this.toDateTime(date).startOf('day');
    return dateTest < today;
  }

  /**
   * Retornar se a data informada é posterior à data atual, ignorando a hora
   */
  static isAfterToday(date: DateLike, timezone?: string): boolean {
    const today = DateUtils.currentDate(timezone);
    const dateTest = this.toDateTime(date).startOf('day');
    return dateTest > today;
  }

  /**
   * Retornar se a data da esquerda é anterior à data da direita
   * @param dateLeft
   */
  static isBefore(dateLeft: DateLike, dateRight: DateLike): boolean {
    const dt1 = this.toDateTime(dateLeft);
    const dt2 = this.toDateTime(dateRight);
    return dt1 < dt2;
  }

  /**
   * Retornar se a primeira data é Posterior à segunda
   */
  static isAfter(dateLeft: DateLike, dateRight: DateLike): boolean {
    const dt1 = this.toDateTime(dateLeft);
    const dt2 = this.toDateTime(dateRight);
    return dt1 > dt2;
  }

  /**
   * Transformar data informada em ISO String
   * @param date DateTime | Date | string
   * @returns string
   */
  static toISO(date: DateLike): string {
    return this.toDateTime(date).toISO() ?? '';
  }

  /**
   * Transformar data informada em ISO Date String, isto é,
   * apenas a data, sem a hora, no formato ISO em string.
   * @param date DateTime | Date | string
   * @returns string
   */
  static toISODate(date: DateLike): string {
    return this.toDateTime(date).toISODate() ?? '';
  }

  /**
   * Transformar data informada em ISO String, sem os milissegundos
   * @param date DateTime | Date | string
   * @returns string
   */
  static toISOWithoutMilliseconds(date: DateLike): string {
    return this.toDateTime(date).set({ second: 0, millisecond: 0 }).toISO() ?? '';
  }

  /**
   * Transformar data informada em ISO Date String UTC, isto é,
   * apenas a data, sem a hora, no formato ISO em string.
   * @param date DateTime | Date | string
   * @returns string
   */
  static toISODateUTC(date: DateLike): string {
    return this.toDateTimeUTC(date).toISODate() ?? '';
  }

  /**
   * Retornar se as datas e horas informadas são iguais
   * @param ignoreTime (opcional) Ignorar horário na comparação (dafault = false)
   */
  static equals(date1: DateLike, date2: DateLike, ignoreTime = false): boolean {
    date1 = this.toDateTime(date1);
    date2 = this.toDateTime(date2);
    if (ignoreTime) {
      return date1.toISODate() === date2.toISODate();
    }
    return +date1 === +date2;
  }

  /**
   * Transformar data informada em Date
   */
  static toJSDate(date: DateLike): Date {
    return this.toDateTime(date)?.toJSDate();
  }

  /**
   * Transformar a data informada em Locale String
   * @param date DateTime | Date | string
   * @returns string
   */
  static toLocaleString(date: DateLike): string {
    return this.toDateTime(date).toLocaleString();
  }

  /**
   * Retornar a data informada mas sem a hora no formato DateTime
   */
  static truncDate(date: DateLike, timezone?: string): DateTime {
    return this.startOfDay(date, timezone).startOf('day');
  }

  /**
   * Transformar data informada em UTC e retorná-la com a hora inicial do dia
   */
  static truncDateUTC(date: DateLike): DateTime {
    return this.toDateTimeUTC(date).startOf('day');
  }

  /**
   * Retornar a data informada com horário zerado
   * @param date DateTime | Date | string
   * @returns DateTime
   */
  static startOfDay(date: DateLike, timezone?: string): DateTime {
    if (!timezone) {
      return this.toDateTime(date).startOf('day');
    }
    return this.toDateTime(date).setZone(timezone).startOf('day');
  }

  static startOfDayUTC(date: DateLike): DateTime {
    return this.toDateTimeUTC(date).startOf('day');
  }

  /**
   * Retornar a data informada com horário 23:59:59.999
   * @param date DateTime | Date | string
   * @returns DateTime
   */
  static endOfDay(date: DateLike, timezone?: string): DateTime {
    if (!timezone) {
      return this.toDateTime(date).endOf('day');
    }
    return this.toDateTime(date).setZone(timezone).endOf('day');
  }

  /**
   * Retornar em UTC a data informada com horário 23:59:59.999
   * @param date DateTime | Date | string
   * @returns DateTime
   */
  static endOfDayUTC(date: DateLike): DateTime {
    return this.toDateTimeUTC(date).endOf('day');
  }

  static endOfMonth(date: DateLike): DateTime {
    return this.toDateTime(date).endOf('month');
  }

  /**
   * Retornar em UTC o último dia do mês da data informada com horário 23:59:59.999
   * @param date DateTime | Date | string
   * @returns DateTime
   */
  static endOfMonthUTC(date: DateLike): DateTime {
    return this.toDateTimeUTC(date).endOf('month');
  }

  /**
   * Retornar em UTC o último dia do ano da data informada com horário 23:59:59.999
   * @param date DateTime | Date | string
   * @returns DateTime
   */
  static endOfYearUTC(date: DateLike): DateTime {
    return this.toDateTimeUTC(date).endOf('year');
  }

  static endOfYear(date: DateLike): DateTime {
    return this.toDateTime(date).endOf('year');
  }

  /**
   * Retornar se a data informada é o último dia do respectivo mês
   */
  static isEndOfMonth(date: DateLike): boolean {
    const dateToValidate = this.toDateTime(date);
    const endOfMonth = dateToValidate.endOf('month');
    return dateToValidate.day === endOfMonth.day;
  }

  /**
   * Retornar se o período informado corresponde exatamente a um mês inteiro, isto é,
   * do primeiro instante do primeiro dia ao último instante (23:59:59.999) do último
   * dia do mesmo mês/ano.
   */
  static isFullMonth(start: DateLike, end: DateLike): boolean {
    if (!start || !end) {
      return false;
    }
    const startDt = this.toDateTime(start);
    const endDt = this.toDateTime(end);
    return startDt.hasSame(endDt, 'month') && this.equals(startDt, startDt.startOf('month')) && this.equals(endDt, endDt.endOf('month'));
  }

  /**
   * Retorna se a primeira data é igual a segunda, ignorando o horário
   */
  static sameDate(date1: DateLike, date2: DateLike): boolean {
    date1 = this.truncDate(date1);
    date2 = this.truncDate(date2);
    return +date1 === +date2;
  }

  /**
   * Definir horário de uma data com base em uma string no formato 'HH:MM:SS'
   * Se a string de horário representar um horário inválido lança um erro
   * @param timeString
   */
  private static setTimeFromString(date: DateLike, stringTime?: string): DateTime {
    if (!date) {
      throw new Error('Invalid date');
    }

    if (!stringTime || !this.isValidStringTime(stringTime)) {
      throw new Error('Invalid string time');
    }

    date = this.toDateTime(date);
    const splitedTime = stringTime.split(':');
    const hour = parseInt(splitedTime[0]);
    const minute = parseInt(splitedTime[1]);
    const second = parseInt(splitedTime[2]);

    date = date.set({ hour, minute, second, millisecond: 0 });
    return date;
  }

  /**
   * Definir horário de uma data com base no horário de outra data ou
   * com base em uma string no formato 'HH:MM:SS'
   */
  static setTime(date: DateLike, time: DateLike, throwError = true): DateTime {
    if (!date || !time) {
      if (throwError) {
        throw new Error('Invalid date');
      }
      return undefined as DateTime;
    }

    if (typeof time === 'string') {
      return this.setTimeFromString(date, time);
    }

    const dateforDate = DateUtils.toDateTime(date);
    const dateForTime = DateUtils.toDateTime(time);
    return dateforDate.set({
      hour: dateForTime.hour,
      minute: dateForTime.minute,
      second: dateForTime.second,
      millisecond: dateForTime.millisecond,
    });
  }

  /**
   * Redefine hora, minuto, segundo e milisegundos da data informada, retornando um DateTime
   */
  static setHour(date: DateLike, hour: number, minute: number, second: number, millisecond: number): DateTime {
    date = this.toDateTime(date);
    date = date.set({ hour, minute, second, millisecond });
    return date;
  }

  /**
   * Copia hora, minuto, segundo e milissegundo de um DateLike para outro DateLike, sem alterar a data
   * @param from Data de onde copiar hora, minuto, segundo e milissegundo
   * @param to Data de destino (a data será mantida, apenas o horário será alterado)
   * @returns DateTime
   */
  static copyHour(from: DateLike, to: DateLike): DateTime {
    const fromDt = this.toDateTime(from);
    const toDt = this.toDateTime(to);
    return toDt.set({
      hour: fromDt.hour,
      minute: fromDt.minute,
      second: fromDt.second,
      millisecond: fromDt.millisecond,
    });
  }

  /**
   * Validar se uma string representa um horário válido no formato HH:MM:SS
   */
  private static isValidStringTime(stringTime: string): boolean {
    const splitedTime = stringTime.split(':');

    return (
      splitedTime.length === 3 &&
      splitedTime.every((time) => time.length === 2) &&
      splitedTime.every((time) => parseInt(time) >= 0) &&
      splitedTime.every((time) => parseInt(time) <= 59) &&
      parseInt(splitedTime[0]) <= 23
    );
  }

  static toDateTimeFromFormat(date: string, format: string, timezone?: string): DateTime {
    return DateTime.fromFormat(date, format, { zone: timezone });
  }

  /**
   * Retornar se o dia da semana informado é válido, isto é, se está entre 1 e 7,
   * sendo 1 segunda-feira e 7 domingo, conforme padrão ISO WEEK DAY
   */
  static isValidWeekday(day: number) {
    return day >= 1 && day <= 7;
  }

  static validateWeekday(day: number) {
    if (!DateUtils.isValidWeekday(day)) {
      throw new Error('Weekdays must be between 1 and 7, with 1 being Monday and 7 being Sunday');
    }
  }

  /**
   * Retornar se a data informada está dentro do período informado
   */
  static isBetween(date: DateLike, start: DateLike, end: DateLike) {
    const dateToCompare = DateUtils.toDateTimeUTC(date);
    const startPeriod = DateUtils.startOfDayUTC(start);
    const endPeriod = DateUtils.endOfDayUTC(end);
    return dateToCompare >= startPeriod && dateToCompare <= endPeriod;
  }

  /**
   * Retornar se o algum dia do primeiro período está dentro do segundo.
   * @param allPeriod Se for true verifica se o primeiro inteiro está dentro do segundo por inteiro
   */
  static isPeriodBetween(startLeft: DateLike, endLeft: DateLike, startRight: DateLike, endRight: DateLike, allPeriod = false) {
    if (!startLeft || !endLeft || !startRight || !endRight) {
      return false;
    }
    const startToCompare = DateUtils.toDateTimeUTC(startLeft);
    const endToCompare = DateUtils.toDateTimeUTC(endLeft);
    const startPeriod = DateUtils.startOfDayUTC(startRight);
    const endPeriod = DateUtils.endOfDayUTC(endRight);
    if (allPeriod) {
      return startToCompare >= startPeriod && endToCompare <= endPeriod;
    }
    return startToCompare <= endPeriod && endToCompare >= startPeriod;
  }

  /**
   *
   * Retorna a menor e maior data de um array de objetos com a propriedade date
   *
   */
  static getMinAndMaxDatesObjectArray<T extends { date?: Date | string }>(
    array: T[],
  ):
    | {
        min: DateTime;
        max: DateTime;
      }
    | undefined {
    if (!array?.length) {
      return undefined;
    }

    const dates = array.map((e) => DateUtils.toDateTime(e.date));
    const min = DateTime.min(...dates);
    const max = DateTime.max(...dates);
    if (!min || !max) return undefined;
    return { min, max };
  }

  /**
   *
   * Retornar string apenas com a data (sem a hora) formatada conforme locale
   *
   */
  static getDateForLocale(date: Date | DateTime, locale: string): string {
    const opt: Intl.DateTimeFormatOptions = { year: '2-digit', month: '2-digit', day: '2-digit' };
    return DateUtils.toDateTime(date).setLocale(locale).toLocaleString(opt);
  }

  /**
   *
   * Retornar string apenas com a hora e minuto (hh:ss) formatada conforme locale
   *
   */
  static getTimeForLocale(date: Date | DateTime, locale: string): string {
    const opt: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    return DateUtils.toDateTime(date).setLocale(locale).toLocaleString(opt);
  }

  static getTimezone(): string {
    return DateTime.local().zoneName;
  }

  static getTimezones(): string[] {
    return listTimeZones();
  }

  /**
   * Converter data para formato UTC
   * @param date Data a ser convertida
   */
  static convertToLocalTime(date: Date): Date {
    const zone = DateTime.local().zoneName;
    const dateUtc = DateUtils.toDateTimeUTC(date)?.toString();
    return DateTime.fromISO(dateUtc, { zone: 'utc' }).setZone(zone).toJSDate();
  }

  static getFirstAndLastDayOfCurrentMonth(): { startDay: DateTime; endDay: DateTime } {
    const now = new Date();

    const startDay = DateUtils.truncDate(new Date(now.getFullYear(), now.getMonth(), 1)).startOf('month');
    const endDay = DateUtils.truncDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)).endOf('month');

    return { startDay, endDay };
  }

  /**
   * Converter uma data em string localizada, exibindo apenas a data ou data e hora
   * conforme a presença de informações de horário.
   * @param date Data para ser formatada
   * @param locale locale Locale utilizado na formatação (padrão: 'pt-br').
   * @returns String formatada ou vazia caso a data não seja informada.
   */
  static getFormattedDateTime(date?: Date | DateTime, locale = 'pt-br'): string {
    if (!date) {
      return '';
    }

    const dateTime = DateUtils.toDateTime(date);
    const hasTime = dateTime.hour + dateTime.minute + dateTime.second > 0;
    const localeString = hasTime ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT;
    return dateTime.setLocale(locale).toLocaleString(localeString);
  }

  /**
   * Converter uma data para um timezone específico
   */
  static toTimezone(date: DateLike, timezone: string): DateTime {
    const dateTime = DateUtils.toDateTime(date);
    return dateTime.setZone(timezone);
  }
}
